import { useState } from "react";
import { toast } from "react-toastify";

import {
  toUpdateWorkTaskModel,
  WorkTaskModel,
} from "../../../../../../factories/work/model/work-task.model";
import { useAppSelector } from "../../../../../../redux/redux";
import {
  useLazyGetWorkQuery,
  useUpdateStatusWorkMutation,
} from "../../../../../../slices/WorkSlice";
import {
  useMarkDoneAllWorkTasksMutation,
  useUpdateWorkTaskMutation,
  useUpdateWorkTaskStatusMutation,
} from "../../../../../../slices/WorkTasksSlice";
import { useGetWorkTypeQuery } from "../../../../../../slices/WorkTypeSlice";
import {
  buildSnapshot,
  clearSnapshot,
  planUntickAll,
  readSnapshot,
  saveSnapshot,
} from "./subtaskBulk.utils";

/**
 * The "tick all subtasks" checkbox, shared by the work item Summary and the
 * Work page's expanded subtask list.
 * - Tick all: every subtask and the work item become Completed (statuses from
 *   just before are remembered in this browser).
 * - Untick all (when any subtask is ticked): ticks are removed and the
 *   subtasks / work item go back to their earlier status (Roadmap 1787).
 */
export const useToggleAllSubtasks = ({
  workId,
  workTypeId,
  workTasks,
  onDone,
}: {
  workId: string | undefined;
  workTypeId: string | undefined;
  workTasks: WorkTaskModel[];
  onDone?: () => unknown;
}) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const [isBusy, setIsBusy] = useState(false);

  const { data: workType } = useGetWorkTypeQuery(
    { id: workTypeId || "" },
    { skip: !workTypeId },
  );
  const completedStatusId =
    workType?.serviceStatuses?.find((status) => status.name === "Completed")
      ?.id || null;

  const [getWork] = useLazyGetWorkQuery();
  const [markDoneAllWorkTasks] = useMarkDoneAllWorkTasksMutation();
  const [updateWorkTaskStatus] = useUpdateWorkTaskStatusMutation();
  const [updateWorkTask] = useUpdateWorkTaskMutation();
  const [updateStatusWork] = useUpdateStatusWorkMutation();

  const setAllIsDone = async (isDone: boolean) => {
    for (const workTask of workTasks) {
      await updateWorkTaskStatus({
        taskId: workTask.id,
        isDone,
        updatedByUserId: user.id,
      }).unwrap();
    }
  };

  const tickAll = async (id: string, completedId: string) => {
    const work = await getWork({ workId: id }).unwrap();

    saveSnapshot(
      id,
      buildSnapshot(
        { statusId: work.jobStatusId, subStatusId: work.jobSubStatusId },
        workTasks,
      ),
    );

    for (const workTask of workTasks) {
      await updateWorkTask(
        toUpdateWorkTaskModel(workTask, {
          statusId: completedId,
          subStatusId: null,
        }),
      ).unwrap();
    }

    await markDoneAllWorkTasks({ workId: id }).unwrap();

    if (work.jobStatusId !== completedId) {
      await updateStatusWork({
        workId: id,
        statusId: completedId,
        subStatusId: "",
      }).unwrap();
    }
  };

  const untickAll = async (id: string) => {
    await setAllIsDone(false);

    const snapshot = readSnapshot(id);
    const work = await getWork({ workId: id }).unwrap();
    const plan = planUntickAll({
      snapshot,
      tasks: workTasks,
      completedStatusId,
      work: { statusId: work.jobStatusId, subStatusId: work.jobSubStatusId },
      serverPreviousStatusId: work.previousJobStatusId,
    });

    for (const restore of plan.tasks) {
      const workTask = workTasks.find((task) => task.id === restore.id);

      if (workTask) {
        await updateWorkTask(
          toUpdateWorkTaskModel(workTask, {
            statusId: restore.statusId,
            subStatusId: restore.subStatusId,
          }),
        ).unwrap();
      }
    }

    if (plan.work?.statusId) {
      await updateStatusWork({
        workId: id,
        statusId: plan.work.statusId,
        subStatusId: plan.work.subStatusId || "",
      }).unwrap();
    }

    clearSnapshot(id);
  };

  const toggleAll = async () => {
    if (!workId || !workTasks.length || isBusy) {
      return;
    }

    const hasCheckedTasks = workTasks.some((task) => task.isDone);

    if (!hasCheckedTasks && !completedStatusId) {
      toast("Completed status was not found", {
        type: "error",
        pauseOnHover: false,
      });

      return;
    }

    setIsBusy(true);

    try {
      if (hasCheckedTasks) {
        await untickAll(workId);
      } else if (completedStatusId) {
        await tickAll(workId, completedStatusId);
      }

      await onDone?.();
      // SUB-016: the server may change the work's status itself (unticking a Completed
      // work's subtasks); read it again so the Details card shows it at once.
      await getWork({ workId })
        .unwrap()
        .catch(() => undefined);
    } catch {
      toast("Something went wrong while updating subtasks", {
        type: "error",
        pauseOnHover: false,
      });
    } finally {
      setIsBusy(false);
    }
  };

  return { toggleAll, isBusy, completedStatusId };
};
